import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { MoneyInput } from './MoneyInput';
import { Wallet, HandCoins } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmtCurrency = (n) => '$ ' + Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => { if (!iso) return '-'; const p = String(iso).slice(0, 10).split('-'); return p.length === 3 ? p.reverse().join('/') : iso; };

/**
 * v3.37.2 — Aplica el saldo a favor del proveedor a uno o varios comprobantes pendientes.
 * Consume FIFO desde las OPs con `advance_amount > 0` y menor `created_at`.
 */
export function SupplierApplyAdvanceDialog({ open, onOpenChange, supplierId, supplierName, availableAdvance = 0, onDone }) {
  const [advances, setAdvances] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !supplierId) return;
    (async () => {
      setLoading(true);
      try {
        const [a, p] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/supplier-accounts/${supplierId}/advances`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/supplier-accounts/${supplierId}/pending`, { withCredentials: true }),
        ]);
        setAdvances(a.data?.items || []);
        setPending((p.data || []).map(x => ({ ...x, selected: false, payAmount: x.outstanding })));
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Error al cargar datos del proveedor');
      } finally { setLoading(false); }
    })();
  }, [open, supplierId]);

  const totalAvailable = advances.reduce((s, a) => s + (a.advance_remaining || 0), 0);
  const togglePending = (pid) => setPending(prev => prev.map(p => p.purchase_invoice_id === pid ? { ...p, selected: !p.selected } : p));
  const updateAmount = (pid, val) => setPending(prev => prev.map(p => p.purchase_invoice_id === pid ? { ...p, payAmount: val } : p));
  const allocTotal = pending.filter(p => p.selected).reduce((s, p) => s + (parseFloat(p.payAmount) || 0), 0);

  const handleApply = async () => {
    const selected = pending.filter(p => p.selected && parseFloat(p.payAmount) > 0);
    if (selected.length === 0) { toast.error('Seleccioná al menos un comprobante'); return; }
    for (const p of selected) {
      if (parseFloat(p.payAmount) > p.outstanding + 0.01) {
        toast.error(`La aplicación a ${p.label} supera su saldo pendiente`); return;
      }
    }
    if (allocTotal > totalAvailable + 0.01) {
      toast.error(`El total a aplicar supera el saldo a favor disponible (${fmtCurrency(totalAvailable)})`); return;
    }
    setSaving(true);
    try {
      const payload = {
        allocations: selected.map(p => ({ purchase_invoice_id: p.purchase_invoice_id, amount: parseFloat(p.payAmount) })),
      };
      const r = await axios.post(`${BACKEND_URL}/api/supplier-accounts/${supplierId}/apply-advance`, payload, { withCredentials: true });
      toast.success(`Se aplicaron ${fmtCurrency(r.data?.applied_total || 0)} a ${selected.length} comprobante(s)`);
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al aplicar el saldo a favor');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[88vh] overflow-y-auto"
        data-testid="supplier-apply-advance-dialog"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HandCoins className="w-5 h-5 text-emerald-600" />
            Aplicar saldo a favor — {supplierName}
          </DialogTitle>
          <DialogDescription>
            Imputá el saldo a favor del proveedor a uno o varios comprobantes pendientes. Se consume por antigüedad (FIFO).
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="py-10 text-center text-muted-foreground">Cargando…</p>
        ) : (
          <div className="space-y-4">
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
              <div className="flex items-center justify-between">
                <span className="font-semibold">Saldo a favor disponible:</span>
                <span className="text-lg font-bold text-emerald-700" data-testid="advance-available">{fmtCurrency(totalAvailable)}</span>
              </div>
              {advances.length > 0 && (
                <div className="mt-2 pt-2 border-t border-emerald-100 text-xs space-y-1" data-testid="advance-origins">
                  <div className="font-semibold text-emerald-800 mb-1">Origen (consumo FIFO):</div>
                  {advances.map(a => (
                    <div key={a.payment_order_id} className="flex justify-between">
                      <span>
                        OP <span className="font-mono">{a.payment_order_number}</span> · {fmtDate(a.date)} · {a.cash_bank_name || ''}
                      </span>
                      <span className="font-medium">{fmtCurrency(a.advance_remaining)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label className="text-sm font-semibold mb-2 block">Comprobantes pendientes</Label>
              {pending.length === 0 ? (
                <p className="text-sm text-muted-foreground">Este proveedor no tiene comprobantes pendientes.</p>
              ) : (
                <div className="space-y-2 border rounded-md p-3" data-testid="apply-advance-pending-list">
                  {pending.map(p => (
                    <div key={p.purchase_invoice_id} className="flex items-center gap-2 text-sm" data-testid={`apply-advance-row-${p.purchase_invoice_id}`}>
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={p.selected}
                        onChange={() => togglePending(p.purchase_invoice_id)}
                        data-testid={`apply-advance-check-${p.purchase_invoice_id}`}
                      />
                      <span className="flex-1">{p.label} <span className="text-xs text-muted-foreground">(saldo {fmtCurrency(p.outstanding)})</span></span>
                      <MoneyInput
                        className="h-8 w-32"
                        value={p.payAmount}
                        disabled={!p.selected}
                        onChange={(e) => updateAmount(p.purchase_invoice_id, e.target.value)}
                        data-testid={`apply-advance-amount-${p.purchase_invoice_id}`}
                      />
                    </div>
                  ))}
                  <div className="text-right text-sm font-semibold pt-2 border-t mt-2" data-testid="apply-advance-total">
                    Total a aplicar: {fmtCurrency(allocTotal)}
                  </div>
                  {allocTotal > totalAvailable + 0.009 && (
                    <div className="text-sm bg-red-50 border border-red-200 rounded px-3 py-2 mt-1" data-testid="apply-advance-over-banner">
                      ⚠️ El total a aplicar supera el saldo a favor disponible.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancelar</Button>
          <Button
            onClick={handleApply}
            disabled={saving || loading || allocTotal <= 0 || allocTotal > totalAvailable + 0.009}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="apply-advance-confirm"
          >
            {saving ? 'Aplicando…' : <><Wallet className="w-4 h-4 mr-1" /> Aplicar</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
